import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CONFIG from '../config';

function RealisationsPage({ token }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [availableForwards, setAvailableForwards] = useState([]);
  const [forwardAllocations, setForwardAllocations] = useState([]);
  const [availablePcfc, setAvailablePcfc] = useState([]);
  const [pcfcAllocations, setPcfcAllocations] = useState([]);
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterSettlement, setFilterSettlement] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    remittanceType: 'NORMAL',
    customerCode: '', bank: '', invoiceId: '', orderId: '',
    realisationDate: '', totalFcRealised: '',
    spotFc: '', spotRate: '',
    discountedFc: '', loanRepaymentRate: '',
    isFinalSettlement: false, notes: ''
  });

  // Format money amounts with commas and 2 decimals (e.g. 10,000.00)
  const fmt = (v) =>
    Number(v || 0).toLocaleString('en-IN',
      { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    loadRecords();
    loadCustomers(false);
    loadBanks();
  }, []);

  const loadRecords = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${CONFIG.API}/api/bills/realisations`, {
        headers: { authorization: `Bearer ${token}` }
      });
      setRecords(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // advanceOnly = true -> list only customers that have an advance expected
  const loadCustomers = async (advanceOnly = false) => {
    try {
      const url = `${CONFIG.API}/api/bills/realisations/customers` +
        (advanceOnly ? '?advanceOnly=true' : '');
      const res = await axios.get(url, {
        headers: { authorization: `Bearer ${token}` }
      });
      setCustomers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadBanks = async () => {
    try {
      const res = await axios.get(`${CONFIG.API}/api/bills/banks`, {
        headers: { authorization: `Bearer ${token}` }
      });
      setBanks(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadInvoices = async (customerCode) => {
    if (!customerCode) { setInvoices([]); return; }
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/realisations/invoices/${customerCode}`,
        { headers: { authorization: `Bearer ${token}` } }
      );
      setInvoices(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadOrders = async (customerCode) => {
    if (!customerCode) { setOrders([]); return; }
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/realisations/orders/${customerCode}`,
        { headers: { authorization: `Bearer ${token}` } }
      );
      setOrders(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  // Normal -> forwards matching the realisation (transaction) date.
  // Advance -> forwards matching the realisation date for the order currency.
  const loadAvailableForwards = async (invoiceId, orderId, realisationDate) => {
    try {
      let url = '';
      if (invoiceId && realisationDate) {
        url = `${CONFIG.API}/api/bills/realisations/available-forwards-date/${invoiceId}/${realisationDate}`;
      } else if (orderId && realisationDate) {
        url = `${CONFIG.API}/api/bills/realisations/available-forwards-order/${orderId}/${realisationDate}`;
      } else {
        setAvailableForwards([]); return;
      }
      const res = await axios.get(url, {
        headers: { authorization: `Bearer ${token}` }
      });
      setAvailableForwards(res.data);
      setForwardAllocations([]);
    } catch (err) {
      console.error(err);
      setAvailableForwards([]);
    }
  };

  // PCFC loans matching currency + bank (oldest first).
  const loadAvailablePcfc = async (invoiceId, orderId, bank) => {
    if (!bank || (!invoiceId && !orderId)) { setAvailablePcfc([]); return; }
    try {
      let url = '';
      if (invoiceId) {
        url = `${CONFIG.API}/api/bills/realisations/available-pcfc/${invoiceId}`;
      } else {
        url = `${CONFIG.API}/api/bills/realisations/available-pcfc-order/${orderId}`;
      }
      const res = await axios.get(url, {
        params: { bank },
        headers: { authorization: `Bearer ${token}` }
      });
      setAvailablePcfc(res.data);
      setPcfcAllocations([]);
    } catch (err) {
      console.error(err);
      setAvailablePcfc([]);
    }
  };

  // For a discounted invoice — auto-fill the loan repayment (read-only).
  const loadDiscountingSummary = async (invoiceId) => {
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/realisations/discounting-summary/${invoiceId}`,
        { headers: { authorization: `Bearer ${token}` } }
      );
      const fc   = parseFloat(res.data.fcDiscounted || 0);
      const rate = parseFloat(res.data.discountRate || 0);
      setForm(prev => ({
        ...prev,
        discountedFc:      fc > 0 ? fc   : '',
        loanRepaymentRate: fc > 0 ? rate : ''
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleChange = (e) => {
    const updated = { ...form, [e.target.name]: e.target.value };
    setForm(updated);

    if (e.target.name === 'remittanceType') {
      setForm({
        ...updated, customerCode: '', invoiceId: '', orderId: '',
        discountedFc: '', loanRepaymentRate: '', isFinalSettlement: false
      });
      setAvailableForwards([]);
      setForwardAllocations([]);
      setAvailablePcfc([]);
      setPcfcAllocations([]);
      setInvoices([]);
      setOrders([]);
      loadCustomers(e.target.value === 'ADVANCE');
    }

    if (e.target.name === 'customerCode') {
      setForm({
        ...updated, invoiceId: '', orderId: '',
        discountedFc: '', loanRepaymentRate: '', isFinalSettlement: false
      });
      setAvailableForwards([]);
      setForwardAllocations([]);
      setAvailablePcfc([]);
      setPcfcAllocations([]);
      if (updated.remittanceType === 'NORMAL') loadInvoices(e.target.value);
      else loadOrders(e.target.value);
    }

    if (e.target.name === 'invoiceId' && e.target.value) {
      loadAvailableForwards(e.target.value, null, updated.realisationDate);
      loadDiscountingSummary(e.target.value);
      if (updated.bank) loadAvailablePcfc(e.target.value, null, updated.bank);
    }

    if (e.target.name === 'orderId' && e.target.value) {
      loadAvailableForwards(null, e.target.value, updated.realisationDate);
      if (updated.bank) loadAvailablePcfc(null, e.target.value, updated.bank);
    }

    if (e.target.name === 'realisationDate') {
      if (updated.remittanceType === 'NORMAL' && updated.invoiceId) {
        loadAvailableForwards(updated.invoiceId, null, e.target.value);
      }
      if (updated.remittanceType === 'ADVANCE' && updated.orderId) {
        loadAvailableForwards(null, updated.orderId, e.target.value);
      }
    }

    if (e.target.name === 'bank') {
      if (updated.remittanceType === 'NORMAL' && updated.invoiceId) {
        loadAvailablePcfc(updated.invoiceId, null, e.target.value);
      }
      if (updated.remittanceType === 'ADVANCE' && updated.orderId) {
        loadAvailablePcfc(null, updated.orderId, e.target.value);
      }
    }
  };

  const handleForwardAllocationChange = (forwardId, value) => {
    const existing = forwardAllocations.find(a => a.forwardId === forwardId);
    if (existing) {
      setForwardAllocations(forwardAllocations.map(a =>
        a.forwardId === forwardId ? { ...a, fcAllocated: value } : a
      ));
    } else {
      const fwd = availableForwards.find(f => f.forwardId === forwardId);
      setForwardAllocations([...forwardAllocations, {
        forwardId,
        fcAllocated: value,
        forwardRate: fwd?.forwardRate || 0
      }]);
    }
  };

  const handlePcfcAllocationChange = (pcfcId, value) => {
    const existing = pcfcAllocations.find(a => a.pcfcId === pcfcId);
    if (existing) {
      setPcfcAllocations(pcfcAllocations.map(a =>
        a.pcfcId === pcfcId ? { ...a, fcAllocated: value } : a
      ));
    } else {
      setPcfcAllocations([...pcfcAllocations, { pcfcId, fcAllocated: value }]);
    }
  };

  const resetForm = () => {
    setForm({
      remittanceType: 'NORMAL',
      customerCode: '', bank: '', invoiceId: '', orderId: '',
      realisationDate: '', totalFcRealised: '',
      spotFc: '', spotRate: '',
      discountedFc: '', loanRepaymentRate: '',
      isFinalSettlement: false, notes: ''
    });
    setForwardAllocations([]);
    setAvailableForwards([]);
    setPcfcAllocations([]);
    setAvailablePcfc([]);
    setInvoices([]);
    setOrders([]);
    setEditingId(null);
    loadCustomers(false);
    setShowForm(false);
  };

  // Get selected invoice / order details
  const selectedInvoice = invoices.find(
    i => String(i.invoiceId) === String(form.invoiceId)
  );

  const selectedOrder = orders.find(
    o => String(o.orderId) === String(form.orderId)
  );

  // Detect discounting scenario
  const isFullyDiscounted = selectedInvoice &&
    parseFloat(selectedInvoice.fcDiscounted) >= parseFloat(selectedInvoice.fcAmount);
  const isPartlyDiscounted = selectedInvoice &&
    parseFloat(selectedInvoice.fcDiscounted) > 0 &&
    parseFloat(selectedInvoice.fcDiscounted) < parseFloat(selectedInvoice.fcAmount);
  const isNotDiscounted = selectedInvoice &&
    parseFloat(selectedInvoice.fcDiscounted) === 0;

  // Effective loan repayment for the discounted portion = the discounted amount,
  // but capped at what was actually received. If less came in than was discounted,
  // the gap is the shortfall and must NOT be forced into the allocation.
  const discountedFcNum = parseFloat(form.discountedFc || 0);
  const receivedFcNum   = parseFloat(form.totalFcRealised || 0);
  const loanRepaymentFc = receivedFcNum > 0
    ? Math.min(discountedFcNum, receivedFcNum)
    : discountedFcNum;

  // Calculate totals
  const calcTotals = () => {
    const spotFc    = parseFloat(form.spotFc   || 0);
    const loanFc    = loanRepaymentFc;
    const forwardFc = forwardAllocations
      .reduce((s, a) => s + parseFloat(a.fcAllocated || 0), 0);
    const pcfcFc    = pcfcAllocations
      .reduce((s, a) => s + parseFloat(a.fcAllocated || 0), 0);
    const totalAllocated = spotFc + pcfcFc + loanFc + forwardFc;
    const fcToRealise    = parseFloat(form.totalFcRealised || 0);
    const remaining      = fcToRealise - totalAllocated;

    // Weighted avg + exchange diff use the OPEN portion only
    // (spot + forward + pcfc). Loan repayment is excluded — its gain/loss was
    // locked at discounting time. PCFC is valued at each loan's draw rate.
    let openWeightedSum = 0;
    let openFc          = 0;
    if (spotFc > 0 && form.spotRate) {
      openWeightedSum += spotFc * parseFloat(form.spotRate);
      openFc          += spotFc;
    }
    forwardAllocations.forEach(a => {
      const fc = parseFloat(a.fcAllocated || 0);
      if (fc > 0) {
        openWeightedSum += fc * parseFloat(a.forwardRate || 0);
        openFc          += fc;
      }
    });
    pcfcAllocations.forEach(a => {
      const fc = parseFloat(a.fcAllocated || 0);
      if (fc > 0) {
        const loan = availablePcfc.find(p => p.pcfcId === a.pcfcId);
        const rate = loan ? parseFloat(loan.drawdownRate || 0) : 0;
        openWeightedSum += fc * rate;
        openFc          += fc;
      }
    });
    const weightedAvg = openFc > 0 ? openWeightedSum / openFc : 0;

    const costRate     = parseFloat(selectedInvoice?.weightedAvgCostRate ||
                                    selectedOrder?.costRate || 0);
    const exchangeDiff = (weightedAvg - costRate) * openFc;

    return {
      fcToRealise, totalAllocated, remaining,
      weightedAvg, exchangeDiff, costRate
    };
  };

  // Projected shortfall (only meaningful when fully settling a normal invoice).
  // = invoice FC minus everything received across all rounds (prior + this one).
  const priorReceived = selectedInvoice
    ? records
        .filter(r => String(r.invoiceId) === String(selectedInvoice.invoiceId))
        .reduce((s, r) => s + parseFloat(r.totalFcRealised || 0), 0)
    : 0;
  const projectedShortfall = selectedInvoice
    ? Math.max(0, parseFloat(selectedInvoice.fcAmount || 0)
        - priorReceived - parseFloat(form.totalFcRealised || 0))
    : 0;

  const handleSubmit = async () => {
    if (!form.customerCode)    { alert('Please select a customer'); return; }
    if (!form.realisationDate) { alert('Please enter realisation date'); return; }
    if (!form.totalFcRealised) { alert('Please enter total FC received'); return; }

    if (form.remittanceType === 'NORMAL' && !form.invoiceId) {
      alert('Please select an invoice'); return;
    }
    if (form.remittanceType === 'ADVANCE' && !form.orderId) {
      alert('Please select an order'); return;
    }

    if (parseFloat(form.spotFc || 0) > 0 && !form.spotRate) {
      alert('Please enter the spot rate'); return;
    }

    const { remaining } = calcTotals();
    if (Math.abs(remaining) > 0.01) {
      alert(`Balance not zero. Remaining FC to allocate: ${remaining.toFixed(2)}`);
      return;
    }

    const payload = {
      remittanceType:    form.remittanceType,
      bank:              form.bank ? form.bank.trim() : null,
      invoiceId:         form.invoiceId ? parseInt(form.invoiceId) : null,
      orderId:           form.orderId   ? parseInt(form.orderId)   : null,
      realisationDate:   form.realisationDate,
      totalFcRealised:   parseFloat(form.totalFcRealised),
      spotFc:            form.spotFc           ? parseFloat(form.spotFc)           : null,
      spotRate:          form.spotRate         ? parseFloat(form.spotRate)         : null,
      loanRepaymentFc:   loanRepaymentFc > 0 ? loanRepaymentFc : null,
      loanRepaymentRate: form.loanRepaymentRate? parseFloat(form.loanRepaymentRate): null,
      isFinalSettlement: !!form.isFinalSettlement,
      notes:             form.notes || null,
      forwardAllocations: forwardAllocations
        .filter(a => parseFloat(a.fcAllocated || 0) > 0)
        .map(a => ({
          forwardId:   a.forwardId,
          fcAllocated: parseFloat(a.fcAllocated)
        })),
      pcfcAllocations: pcfcAllocations
        .filter(a => parseFloat(a.fcAllocated || 0) > 0)
        .map(a => ({
          pcfcId:      a.pcfcId,
          fcAllocated: parseFloat(a.fcAllocated)
        }))
    };

    try {
      if (editingId) {
        await axios.put(`${CONFIG.API}/api/bills/realisations/${editingId}`, payload, {
          headers: { authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${CONFIG.API}/api/bills/realisations`, payload, {
          headers: { authorization: `Bearer ${token}` }
        });
      }
      resetForm();
      loadRecords();
    } catch (err) {
      const msg = err?.response?.data || 'Please check all fields.';
      alert('Error saving realisation. ' + msg);
    }
  };

  const handleSettlementToggle = async (record) => {
    const newStatus = record.settlementStatus === 'SETTLED'
      ? 'NOT_SETTLED' : 'SETTLED';
    try {
      await axios.put(
        `${CONFIG.API}/api/bills/realisations/${record.realisationId}/settlement`,
        { settlementStatus: newStatus },
        { headers: { authorization: `Bearer ${token}` } }
      );
      loadRecords();
    } catch (err) {
      alert('Error updating settlement status.');
    }
  };

  // Load a saved record back into the form for editing
  const startEdit = async (r) => {
    const auth = { headers: { authorization: `Bearer ${token}` } };
    const rdate = (r.realisationDate || '').slice(0, 10);
    try {
      // 1. invoices / orders for this customer
      if (r.remittanceType === 'NORMAL') {
        const inv = await axios.get(
          `${CONFIG.API}/api/bills/realisations/invoices/${r.customerCode}`, auth);
        setInvoices(inv.data);
      } else {
        const ord = await axios.get(
          `${CONFIG.API}/api/bills/realisations/orders/${r.customerCode}`, auth);
        setOrders(ord.data);
      }

      // 2. discounting summary -> discounted FC + rate (normal only)
      let discountedFc = '';
      let loanRate = r.loanRepaymentRate != null && parseFloat(r.loanRepaymentRate) > 0
        ? String(r.loanRepaymentRate) : '';
      if (r.remittanceType === 'NORMAL' && r.invoiceId) {
        try {
          const ds = await axios.get(
            `${CONFIG.API}/api/bills/realisations/discounting-summary/${r.invoiceId}`, auth);
          const fc = parseFloat(ds.data.fcDiscounted || 0);
          if (fc > 0) {
            discountedFc = String(fc);
            if (!loanRate) loanRate = String(ds.data.discountRate || 0);
          }
        } catch (e) { /* ignore */ }
      }

      // 3. available forwards
      let fwds = [];
      if (r.remittanceType === 'NORMAL' && r.invoiceId && rdate) {
        const f = await axios.get(
          `${CONFIG.API}/api/bills/realisations/available-forwards-date/${r.invoiceId}/${rdate}`, auth);
        fwds = f.data;
      } else if (r.remittanceType === 'ADVANCE' && r.orderId && rdate) {
        const f = await axios.get(
          `${CONFIG.API}/api/bills/realisations/available-forwards-order/${r.orderId}/${rdate}`, auth);
        fwds = f.data;
      }

      // 4. available PCFC
      let pcfc = [];
      if (r.bank && r.invoiceId) {
        const p = await axios.get(
          `${CONFIG.API}/api/bills/realisations/available-pcfc/${r.invoiceId}`,
          { ...auth, params: { bank: r.bank } });
        pcfc = p.data;
      } else if (r.bank && r.orderId) {
        const p = await axios.get(
          `${CONFIG.API}/api/bills/realisations/available-pcfc-order/${r.orderId}`,
          { ...auth, params: { bank: r.bank } });
        pcfc = p.data;
      }

      // 5. detail — the exact forwards/PCFC this record used
      const det = await axios.get(
        `${CONFIG.API}/api/bills/realisations/${r.realisationId}/detail`, auth);
      const detFwd  = det.data.forwardAllocations || [];
      const detPcfc = det.data.pcfcAllocations || [];

      // 6. merge this record's own allocations back into availability,
      //    so they still show (they were used up by this very record)
      detFwd.forEach(d => {
        const ex = fwds.find(f => f.forwardId === d.forwardId);
        if (ex) ex.fcAvailable = parseFloat(ex.fcAvailable) + parseFloat(d.fcAllocated);
        else fwds.push({
          forwardId: d.forwardId, dealNo: d.dealNo, bank: d.bank,
          forwardRate: d.forwardRate, fcAvailable: parseFloat(d.fcAllocated),
          windowStartDate: d.windowStartDate, windowEndDate: d.windowEndDate
        });
      });
      detPcfc.forEach(d => {
        const ex = pcfc.find(p => p.pcfcId === d.pcfcId);
        if (ex) ex.fcAvailable = parseFloat(ex.fcAvailable) + parseFloat(d.fcAllocated);
        else pcfc.push({
          pcfcId: d.pcfcId, pcfcRef: d.pcfcRef, pcfcDate: d.pcfcDate,
          drawdownRate: d.drawdownRate, fcAvailable: parseFloat(d.fcAllocated)
        });
      });

      // 7. apply to state
      setAvailableForwards(fwds);
      setAvailablePcfc(pcfc);
      setForwardAllocations(detFwd.map(d => ({
        forwardId: d.forwardId,
        fcAllocated: String(d.fcAllocated),
        forwardRate: d.forwardRate
      })));
      setPcfcAllocations(detPcfc.map(d => ({
        pcfcId: d.pcfcId,
        fcAllocated: String(d.fcAllocated)
      })));

      setForm({
        remittanceType: r.remittanceType,
        customerCode: r.customerCode,
        bank: r.bank || '',
        invoiceId: r.invoiceId ? String(r.invoiceId) : '',
        orderId: r.orderId ? String(r.orderId) : '',
        realisationDate: rdate,
        totalFcRealised: r.totalFcRealised != null ? String(r.totalFcRealised) : '',
        spotFc: r.spotFc != null && parseFloat(r.spotFc) > 0 ? String(r.spotFc) : '',
        spotRate: r.spotRate != null && parseFloat(r.spotRate) > 0 ? String(r.spotRate) : '',
        discountedFc: discountedFc,
        loanRepaymentRate: loanRate,
        isFinalSettlement: r.settlementStatus === 'SETTLED',
        notes: r.notes || ''
      });

      setEditingId(r.realisationId);
      setShowForm(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error(err);
      alert('Could not load this record for editing.');
    }
  };

  // Delete a realisation (the server reverses all its effects)
  const handleDelete = async (r) => {
    const label = r.invoiceNo !== 'ADVANCE' ? r.invoiceNo : r.orderNo;
    const ok = window.confirm(
      `Delete this realisation for ${r.customerCode} (${label})?\n\n` +
      `This will reverse the PCFC repayment, free the forward contracts, ` +
      `and reopen the invoice. This cannot be undone.`);
    if (!ok) return;
    try {
      await axios.delete(
        `${CONFIG.API}/api/bills/realisations/${r.realisationId}`, {
        headers: { authorization: `Bearer ${token}` }
      });
      if (editingId === r.realisationId) resetForm();
      loadRecords();
    } catch (err) {
      const msg = err?.response?.data || '';
      alert('Error deleting realisation. ' + msg);
    }
  };

  const {
    fcToRealise, totalAllocated, remaining,
    weightedAvg, exchangeDiff, costRate
  } = calcTotals();

  // Allocation section — used for both normal and advance
  const renderAllocationSection = (heading) => {
    // Forgiving bank filter: matches "hdfc" against "HDFC Bank" etc.
    const bankFilter = (form.bank || '').trim().toLowerCase();
    const shownForwards = bankFilter
      ? availableForwards.filter(f =>
          (f.bank || '').toLowerCase().includes(bankFilter))
      : availableForwards;

    return (
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', fontWeight: '600',
                      color: '#374151', marginBottom: '10px' }}>
          {heading}
        </div>

        {/* Spot */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                      gap: '12px', marginBottom: '6px' }}>
          <div className="form-field">
            <label>Spot — FC amount</label>
            <input type="number" name="spotFc" value={form.spotFc}
              onChange={handleChange} placeholder="0" />
          </div>
          <div className="form-field">
            <label>Spot rate</label>
            <input type="number" name="spotRate" value={form.spotRate}
              onChange={handleChange} placeholder="e.g. 84.50" />
          </div>
        </div>
        {form.spotFc && form.spotRate && (
          <div style={{ fontSize: '12px', color: '#64748B',
                        marginBottom: '8px', paddingLeft: '4px' }}>
            Spot INR = {fmt(parseFloat(form.spotFc) * parseFloat(form.spotRate))}
          </div>
        )}

        {/* Forward table */}
        {shownForwards.length > 0 && (
          <div style={{ marginBottom: '10px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600',
                          color: '#374151', marginBottom: '6px' }}>
              Forward contracts (matching realisation date)
              {bankFilter && ` — bank "${form.bank.trim()}"`}
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Deal no</th>
                  <th>Bank</th>
                  <th>Forward rate</th>
                  <th>FC available</th>
                  <th>Window</th>
                  <th>Allocate FC</th>
                  <th>INR value</th>
                </tr>
              </thead>
              <tbody>
                {shownForwards.map(fwd => {
                  const alloc   = forwardAllocations.find(
                    a => a.forwardId === fwd.forwardId);
                  const allocFc = parseFloat(alloc?.fcAllocated || 0);
                  const inrVal  = allocFc * parseFloat(fwd.forwardRate);
                  return (
                    <tr key={fwd.forwardId}>
                      <td>{fwd.dealNo}</td>
                      <td>{fwd.bank}</td>
                      <td>{Number(fwd.forwardRate).toFixed(4)}</td>
                      <td>{fmt(fwd.fcAvailable)}</td>
                      <td style={{ fontSize: '11px' }}>
                        {fwd.windowStartDate} to {fwd.windowEndDate}
                      </td>
                      <td>
                        <input
                          type="number"
                          value={alloc?.fcAllocated || ''}
                          onChange={e => handleForwardAllocationChange(
                            fwd.forwardId, e.target.value)}
                          placeholder="0"
                          max={fwd.fcAvailable}
                          style={{
                            width: '100px', padding: '4px 8px',
                            border: '1px solid #CBD5E1',
                            borderRadius: '4px', fontSize: '13px'
                          }}
                        />
                      </td>
                      <td style={{ color: '#166534', fontWeight: '600' }}>
                        {allocFc > 0 ? fmt(inrVal) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Helpful note when a bank is set but nothing matches */}
        {bankFilter && shownForwards.length === 0 && availableForwards.length > 0 && (
          <div style={{ fontSize: '12px', color: '#92400E',
                        marginBottom: '10px', paddingLeft: '4px' }}>
            No forward contracts from a bank matching "{form.bank.trim()}".
          </div>
        )}

        {/* PCFC repayment — real loans, oldest first */}
        <div style={{ marginTop: '6px' }}>
          <div style={{ fontSize: '12px', fontWeight: '600',
                        color: '#374151', marginBottom: '6px' }}>
            PCFC repayment (optional) — oldest loans first
          </div>

          {!form.bank && (
            <div style={{
              background: '#FEF9C3', border: '1px solid #FDE68A',
              borderRadius: '6px', padding: '8px 12px',
              fontSize: '12px', color: '#92400E', marginBottom: '8px'
            }}>
              Select a bank above to see its PCFC loans.
            </div>
          )}

          {form.bank && availablePcfc.length === 0 && (
            <div style={{
              background: '#FEF9C3', border: '1px solid #FDE68A',
              borderRadius: '6px', padding: '8px 12px',
              fontSize: '12px', color: '#92400E', marginBottom: '8px'
            }}>
              No open PCFC loans for this bank and currency.
            </div>
          )}

          {availablePcfc.length > 0 && (
            <table className="data-table">
              <thead>
                <tr>
                  <th>PCFC ref</th>
                  <th>Date</th>
                  <th>Draw rate</th>
                  <th>FC outstanding</th>
                  <th>Repay FC</th>
                </tr>
              </thead>
              <tbody>
                {availablePcfc.map(p => {
                  const alloc = pcfcAllocations.find(a => a.pcfcId === p.pcfcId);
                  return (
                    <tr key={p.pcfcId}>
                      <td>{p.pcfcRef}</td>
                      <td>{p.pcfcDate}</td>
                      <td>{Number(p.drawdownRate).toFixed(4)}</td>
                      <td>{fmt(p.fcAvailable)}</td>
                      <td>
                        <input
                          type="number"
                          value={alloc?.fcAllocated || ''}
                          onChange={e => handlePcfcAllocationChange(
                            p.pcfcId, e.target.value)}
                          placeholder="0"
                          max={p.fcAvailable}
                          style={{
                            width: '100px', padding: '4px 8px',
                            border: '1px solid #CBD5E1',
                            borderRadius: '4px', fontSize: '13px'
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  // Read-only loan repayment block (auto-filled from the discounting)
  const renderLoanRepayment = (heading) => (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontSize: '13px', fontWeight: '600',
                    color: '#374151', marginBottom: '10px' }}>
        {heading}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                    gap: '12px' }}>
        <div className="form-field">
          <label>Loan repayment FC (auto)</label>
          <input value={loanRepaymentFc || ''} readOnly
            style={{ background: '#F1F5F9', color: '#475569' }} />
        </div>
        <div className="form-field">
          <label>Rate — discounted at (auto)</label>
          <input value={form.loanRepaymentRate} readOnly
            style={{ background: '#F1F5F9', color: '#475569' }} />
        </div>
      </div>
      <div style={{ fontSize: '12px', color: '#64748B',
                    marginTop: '6px', paddingLeft: '4px' }}>
        This closes the discounted portion at the rate it was discounted at — no
        new exchange difference.
      </div>
    </div>
  );

  const filteredRecords = records.filter(r => {
    const matchCustomer   = filterCustomer   === '' ||
      r.customerCode.toLowerCase().includes(filterCustomer.toLowerCase());
    const matchSettlement = filterSettlement === '' ||
      r.settlementStatus === filterSettlement;
    return matchCustomer && matchSettlement;
  });

  return (
    <div className="content">

      {/* Summary cards */}
      <div className="metric-grid">
        <div className="metric-card">
          <div className="metric-label">Total realisations</div>
          <div className="metric-value val-blue">{records.length}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Settled</div>
          <div className="metric-value val-green">
            {records.filter(r => r.settlementStatus === 'SETTLED').length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Not settled</div>
          <div className="metric-value val-amber">
            {records.filter(r => r.settlementStatus === 'NOT_SETTLED').length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Advances</div>
          <div className="metric-value val-blue">
            {records.filter(r => r.remittanceType === 'ADVANCE').length}
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="section-header">
        <div className="section-title">Realisation records</div>
        <button className="btn-primary" onClick={() => {
          if (showForm) { resetForm(); } else { setShowForm(true); }
        }}>
          {showForm ? '✕ Close' : '+ New realisation'}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="form-card" style={{ maxWidth: '760px' }}>
          <div className="form-step" style={{ marginBottom: '16px' }}>
            {editingId ? '✏️ Edit realisation' : '📋 New realisation entry'}
          </div>

          {/* Row 1 — Remittance type + Customer + Bank */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                        gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Remittance type</label>
              <select name="remittanceType" value={form.remittanceType}
                onChange={handleChange}>
                <option value="NORMAL">Normal realisation</option>
                <option value="ADVANCE">Advance remittance</option>
              </select>
            </div>
            <div className="form-field">
              <label>Customer code</label>
              <select name="customerCode" value={form.customerCode}
                onChange={handleChange}>
                <option value="">— Select customer —</option>
                {customers.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Bank</label>
              <select name="bank" value={form.bank} onChange={handleChange}>
                <option value="">— Select bank —</option>
                {form.bank && !banks.some(b => b.bankName === form.bank) && (
                  <option value={form.bank}>{form.bank} (old)</option>
                )}
                {banks.map(b => (
                  <option key={b.bankId} value={b.bankName}>
                    {b.bankName}{b.branch ? ' — ' + b.branch : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 2 — Invoice or Order + Date + Total FC */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                        gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>{form.remittanceType === 'NORMAL' ? 'Invoice' : 'Order'}</label>
              {form.remittanceType === 'NORMAL' ? (
                <select name="invoiceId" value={form.invoiceId}
                  onChange={handleChange}>
                  <option value="">— Select invoice —</option>
                  {invoices.map(inv => (
                    <option key={inv.invoiceId} value={inv.invoiceId}>
                      {inv.invoiceNo} — FC open: {fmt(inv.fcOpen)}
                    </option>
                  ))}
                </select>
              ) : (
                <select name="orderId" value={form.orderId}
                  onChange={handleChange}>
                  <option value="">— Select order —</option>
                  {orders.map(ord => (
                    <option key={ord.orderId} value={ord.orderId}>
                      {ord.orderNo} — FC: {fmt(ord.fcAmount)}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="form-field">
              <label>Realisation date</label>
              <input type="date" name="realisationDate"
                value={form.realisationDate} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label>Total FC received</label>
              <input type="number" name="totalFcRealised"
                value={form.totalFcRealised} onChange={handleChange}
                placeholder="0" />
            </div>
          </div>

          {/* Invoice / Order details bar */}
          {selectedInvoice && (
            <div style={{
              background: '#F0F9FF', border: '1px solid #BAE6FD',
              borderRadius: '6px', padding: '10px 14px',
              fontSize: '13px', marginBottom: '14px',
              display: 'flex', gap: '20px', flexWrap: 'wrap'
            }}>
              <span><strong>FC amount:</strong> {fmt(selectedInvoice.fcAmount)}</span>
              <span><strong>FC open:</strong> {fmt(selectedInvoice.fcOpen)}</span>
              <span><strong>FC discounted:</strong> {fmt(selectedInvoice.fcDiscounted)}</span>
              <span><strong>Due date:</strong> {selectedInvoice.dueDate}</span>
              <span><strong>Cost rate:</strong> {Number(selectedInvoice.weightedAvgCostRate).toFixed(4)}</span>
              <span style={{
                fontWeight: '700',
                color: isFullyDiscounted ? '#166534' :
                       isPartlyDiscounted ? '#92400E' : '#1D4ED8'
              }}>
                {isFullyDiscounted ? '✅ Fully discounted' :
                 isPartlyDiscounted ? '⚠️ Partly discounted' :
                 '🔵 Not discounted'}
              </span>
            </div>
          )}

          {selectedOrder && (
            <div style={{
              background: '#F0F9FF', border: '1px solid #BAE6FD',
              borderRadius: '6px', padding: '10px 14px',
              fontSize: '13px', marginBottom: '14px',
              display: 'flex', gap: '20px', flexWrap: 'wrap'
            }}>
              <span><strong>Order FC:</strong> {fmt(selectedOrder.fcAmount)}</span>
              <span><strong>Currency:</strong> {selectedOrder.currency}</span>
              <span><strong>Cost rate:</strong> {Number(selectedOrder.costRate).toFixed(4)}</span>
              {selectedOrder.advanceExpectedFc > 0 && (
                <span><strong>Advance expected:</strong> {fmt(selectedOrder.advanceExpectedFc)}</span>
              )}
            </div>
          )}

          {/* Scenario sections */}

          {/* Fully discounted — loan repayment only (no open portion to split) */}
          {form.remittanceType === 'NORMAL' && selectedInvoice && isFullyDiscounted &&
            renderLoanRepayment('Discounted portion — loan repayment')
          }

          {/* Partly discounted — loan repayment + open portion */}
          {form.remittanceType === 'NORMAL' && selectedInvoice && isPartlyDiscounted && (
            <>
              {renderLoanRepayment('Discounted portion — loan repayment')}
              {renderAllocationSection('Open portion — Spot / Forward / PCFC')}
            </>
          )}

          {/* Not discounted or advance — full allocation */}
          {(form.remittanceType === 'ADVANCE' ||
            (form.remittanceType === 'NORMAL' && selectedInvoice && isNotDiscounted)) &&
            renderAllocationSection('Allocation — Spot / Forward / PCFC')
          }

          {/* Fully settled (final payment) */}
          {form.remittanceType === 'NORMAL' && selectedInvoice && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '10px 14px', marginBottom: '14px',
              border: '1px solid #CBD5E1', borderRadius: '6px',
              background: form.isFinalSettlement ? '#F0FDF4' : '#FFFFFF'
            }}>
              <input
                type="checkbox"
                checked={form.isFinalSettlement}
                onChange={e => setForm({ ...form, isFinalSettlement: e.target.checked })}
                style={{ width: '16px', height: '16px' }}
              />
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                Fully settled (final payment)
              </span>
              <span style={{ fontSize: '12px', color: '#64748B', marginLeft: 'auto' }}>
                {form.isFinalSettlement
                  ? `Invoice will be closed. Shortfall: ${fmt(projectedShortfall)}`
                  : 'Leave unticked if more payment is still expected'}
              </span>
            </div>
          )}

          {/* Balance bar */}
          {fcToRealise > 0 && (
            <div style={{
              background: Math.abs(remaining) < 0.01 ? '#F0FDF4' : '#FEF2F2',
              border: `1px solid ${Math.abs(remaining) < 0.01 ? '#BBF7D0' : '#FCA5A5'}`,
              borderRadius: '6px', padding: '10px 14px',
              fontSize: '13px', marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <span><strong>FC to realise:</strong> {fmt(fcToRealise)}</span>
                <span><strong>Total allocated:</strong> {fmt(totalAllocated)}</span>
                <span style={{
                  color: Math.abs(remaining) < 0.01 ? '#166534' : '#DC2626',
                  fontWeight: '700'
                }}>
                  <strong>Remaining:</strong> {fmt(remaining)}
                </span>
                {weightedAvg > 0 && (
                  <span style={{ color: '#1D4ED8', fontWeight: '600' }}>
                    <strong>Wtd avg realisation rate:</strong> {weightedAvg.toFixed(4)}
                  </span>
                )}
                {weightedAvg > 0 && costRate > 0 && (
                  <span style={{
                    color: exchangeDiff >= 0 ? '#166534' : '#DC2626',
                    fontWeight: '600'
                  }}>
                    <strong>Ex diff:</strong> ₹{fmt(exchangeDiff)}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          <div style={{ marginBottom: '16px' }}>
            <div className="form-field">
              <label>Notes (optional)</label>
              <textarea name="notes" value={form.notes}
                onChange={handleChange} rows={2}
                placeholder="Any additional notes..."
                style={{
                  width: '100%', padding: '8px 10px',
                  border: '1px solid #CBD5E1', borderRadius: '6px',
                  fontSize: '14px', resize: 'vertical',
                  fontFamily: 'inherit', boxSizing: 'border-box'
                }} />
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button className="btn-secondary" onClick={resetForm}>Cancel</button>
            <button className="btn-primary" onClick={handleSubmit}>
              {editingId ? '✓ Update realisation' : '✓ Save realisation'}
            </button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div style={{
        display: 'flex', gap: '12px', alignItems: 'center',
        marginBottom: '12px', flexWrap: 'wrap'
      }}>
        <input
          type="text"
          placeholder="🔍 Search customer..."
          value={filterCustomer}
          onChange={e => setFilterCustomer(e.target.value)}
          style={{
            padding: '7px 12px', border: '1px solid #CBD5E1',
            borderRadius: '6px', fontSize: '14px', width: '200px'
          }}
        />
        <select
          value={filterSettlement}
          onChange={e => setFilterSettlement(e.target.value)}
          style={{
            padding: '7px 12px', border: '1px solid #CBD5E1',
            borderRadius: '6px', fontSize: '14px', color: '#374151'
          }}
        >
          <option value="">All statuses</option>
          <option value="SETTLED">Settled</option>
          <option value="NOT_SETTLED">Not settled</option>
        </select>
        {(filterSettlement !== '' || filterCustomer !== '') && (
          <button
            onClick={() => { setFilterSettlement(''); setFilterCustomer(''); }}
            style={{
              padding: '7px 12px', border: '1px solid #CBD5E1',
              borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
              background: '#F1F5F9', color: '#64748B'
            }}>
            ✕ Clear filters
          </button>
        )}
        <span style={{ fontSize: '13px', color: '#64748B', marginLeft: 'auto' }}>
          Showing {filteredRecords.length} of {records.length} records
        </span>
      </div>

      {/* Records table */}
      <div className="table-wrap" style={{ overflowX: 'auto', width: '100%' }}>
        {loading ? (
          <p style={{ padding: '20px', color: '#64748B' }}>Loading...</p>
        ) : filteredRecords.length === 0 ? (
          <p style={{ padding: '20px', color: '#64748B' }}>
            No realisation records found.
          </p>
        ) : (
          <table className="data-table" style={{ minWidth: '1500px' }}>
            <thead>
              <tr>
                <th>Type</th>
                <th>Invoice / Order</th>
                <th>Invoice amount</th>
                <th>Customer</th>
                <th>Bank</th>
                <th>Currency</th>
                <th>Realisation date</th>
                <th>FC realised</th>
                <th>Wtd avg rate</th>
                <th>Cost rate</th>
                <th>Ex diff ₹</th>
                <th>Shortfall FC</th>
                <th>Shortfall ₹</th>
                <th>Due date</th>
                <th>Early/Delay</th>
                <th>Settlement</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map(r => (
                <tr key={r.realisationId}>
                  <td>
                    <span className={`badge ${r.remittanceType === 'ADVANCE'
                      ? 'badge-partial' : 'badge-open'}`}>
                      {r.remittanceType === 'ADVANCE' ? 'ADVANCE' : 'NORMAL'}
                    </span>
                  </td>
                  <td>{r.invoiceNo !== 'ADVANCE' ? r.invoiceNo : r.orderNo}</td>
                  <td>{Number(r.invoiceFcAmount) > 0 ? fmt(r.invoiceFcAmount) : '—'}</td>
                  <td>{r.customerCode}</td>
                  <td>{r.bank || '—'}</td>
                  <td>{r.currency}</td>
                  <td>{r.realisationDate}</td>
                  <td>{fmt(r.totalFcRealised)}</td>
                  <td>{r.weightedAvgRate > 0
                    ? Number(r.weightedAvgRate).toFixed(4) : '—'}</td>
                  <td>{r.costRate > 0
                    ? Number(r.costRate).toFixed(4) : '—'}</td>
                  <td style={{
                    color: r.exchangeDiff >= 0 ? '#166534' : '#DC2626',
                    fontWeight: '600'
                  }}>
                    {r.weightedAvgRate > 0 ? fmt(r.exchangeDiff) : '—'}
                  </td>
                  <td>{r.shortfallFc > 0 ? fmt(r.shortfallFc) : '—'}</td>
                  <td style={{ color: '#DC2626' }}>
                    {r.shortfallInr > 0 ? fmt(r.shortfallInr) : '—'}
                  </td>
                  <td>{r.dueDate || '—'}</td>
                  <td>
                    {r.daysEarly > 0 && (
                      <span style={{ color: '#166534', fontSize: '12px' }}>
                        ⬆ {r.daysEarly}d early
                      </span>
                    )}
                    {r.daysDelayed > 0 && (
                      <span style={{ color: '#DC2626', fontSize: '12px' }}>
                        ⬇ {r.daysDelayed}d late
                      </span>
                    )}
                    {!r.daysEarly && !r.daysDelayed && r.dueDate && (
                      <span style={{ color: '#166534', fontSize: '12px' }}>
                        ✓ On time
                      </span>
                    )}
                  </td>
                  <td>
                    <button
                      onClick={() => handleSettlementToggle(r)}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '4px',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: r.settlementStatus === 'SETTLED'
                          ? '#DCFCE7' : '#FEF9C3',
                        color: r.settlementStatus === 'SETTLED'
                          ? '#166534' : '#92400E'
                      }}>
                      {r.settlementStatus === 'SETTLED'
                        ? '✓ Settled' : '⏳ Not settled'}
                    </button>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={() => startEdit(r)}
                        title="Edit this realisation"
                        style={{
                          padding: '4px 10px', borderRadius: '4px',
                          border: '1px solid #BAE6FD', cursor: 'pointer',
                          fontSize: '12px', fontWeight: '600',
                          background: '#F0F9FF', color: '#1D4ED8'
                        }}>
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDelete(r)}
                        title="Delete and reverse this realisation"
                        style={{
                          padding: '4px 10px', borderRadius: '4px',
                          border: '1px solid #FCA5A5', cursor: 'pointer',
                          fontSize: '12px', fontWeight: '600',
                          background: '#FEF2F2', color: '#DC2626'
                        }}>
                        🗑 Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
}

export default RealisationsPage;